import { Operation, ProviderType, isNewFlowstepFormat } from 'shared/types/flowstep';
import { capitalizeString, toQueryString, asArray, MultipleErrorsError } from 'shared/utils/utils';
import { v4 as uuid } from 'uuid';


export const RangeType = {
  All: 'all',
  New: 'new',
  Existing: 'existing',
  Selected: 'selected',
  Rows: 'rows'
};

export const FlowstepType = {
  Soql: 'soql',
  Report: 'report',
  Csv: 'csv',
  Sosl: 'sosl',
  Insert: 'insert',
  Upsert: 'upsert',
  Update: 'update',
  Delete: 'delete',
  Undelete: 'undelete',
  ConvertLeads: 'convert-leads',
  MergeObjects: 'merge-objects',
  Hubspot: 'hubspot',
};

export const FlowstepV2Types = [FlowstepType.Hubspot];

export const Type = FlowstepType;

export const InsertFlowsteps = [Type.Insert, Type.Update, Type.Upsert];
export const DeleteFlowsteps = [Type.Delete, Type.Undelete];
export const AdminToolFlowsteps = [Type.ConvertLeads, Type.MergeObjects];

export const PullFlowsteps = [Type.Soql, Type.Report, Type.Csv, Type.Sosl, Type.Objects];
export const PushFlowsteps = [...InsertFlowsteps, ...DeleteFlowsteps, ...AdminToolFlowsteps];
export const AllFlowsteps = [...PullFlowsteps, ...PushFlowsteps];

export const FlowstepCategory = {
  Push: 'push',
  Pull: 'pull'
};

export const Category = FlowstepCategory;

export const ErrorReaction = {
  Stop: 'stop',
  Continue: 'continue',
  AskUser: 'ask-user',
};

export const PullMode = {
  Overwrite: 'overwrite',
  Append: 'append',
  NewSheet: 'new-sheet',
};

export function toVerbForms(type) {
  const getForms = () => {
    switch (type) {
      case Type.Sosl:
      case Type.Soql: return ['queried', 'quering', 'queries', 'query'];
      case Type.Report: return ['reported', 'reporting', 'reports', 'report'];
      case Type.Csv: return ['downloaded', 'downloading', 'downloads', 'download'];
      case Type.Insert: return ['created', 'creating', 'creates', 'create'];
      case Type.Upsert: return ['upserted', 'upserting', 'upserts', 'upsert'];
      case Type.Update: return ['updated', 'updating', 'updates', 'update'];
      case Type.Delete: return ['deleted', 'deleting', 'deletes', 'delete'];
      case Type.Undelete: return ['undeleted', 'undeleting', 'undeletes', 'undelete'];
      case Type.ConvertLeads: return ['converted leads', 'converting leads', 'converts', 'convert leads'];
      case Type.MergeObjects: return ['merged objects', 'merging objects', 'merges', 'merge objects'];
      default: return ['somethed', 'something'];
    }
  }

  const [ed, ing, s, inf] = getForms();
  return { ing, ed, s, inf };
}

export function toDisplayName(type) {
  switch (type) {
    case Type.Report: return 'From a Report'
    case Type.Soql: return 'From Objects and Fields';
    case Type.Sosl: return 'Search in Salesforce';
    case Type.Csv: return 'CSV file';
    case Type.ConvertLeads: return 'Convert leads';
    case Type.MergeObjects: return 'Merge records';
    default: return capitalizeString(type);
  }
}

export function toV2DisplayName(operation, provider) {
  switch (operation) {
    case Operation.PULL:
      switch (provider) {
        case ProviderType.HS:
          return 'From Objects and Fields';
        case ProviderType.HubspotList:
          return 'From a List';
        default:
          return ''
      }
    case Operation.INSERT:
    case Operation.UPDATE:
    case Operation.DELETE:
      return capitalizeString(operation)
    default:
      return '';
  }
}

export function toPageTitle(type) {
  const dn = toDisplayName(type);
  switch (type) {
    case Type.Sosl:
    case Type.Soql: return `Run ${dn}`;
    case Type.Report: return 'Reports';
    case Type.Csv: return `Download ${dn}`;
    case Type.ConvertLeads:
    case Type.MergeObjects: return dn;
    default: return `${dn} Data`;
  }
}

function rangeTypeToDisplayName(type) {
  return `on ${type !== RangeType.Rows ? type : 'specific'}`;
}

export function generateFlowstepName({ flowstep, type, sfObjectName, worksheetName, target } = {}) {
  if (flowstep) {
    const getObjectName = (type) => {
      if (Type.Soql === type || Type.Sosl === type) { return flowstep.options.objectName; }
      if (Type.Report === type) { return flowstep.options.report_name; }
      if (isInsertKind(type)) { return flowstep.options.mapping.sfObjectName; }
      return '';
    };

    type = flowstep.type;
    target = flowstep.target;
    sfObjectName = getObjectName(type);
  }

  const getPreposition = () => {
    switch (type) {
      case Type.Update: return ' of';
      case Type.Upsert:
      case Type.Insert: return ' into';
      case Type.Sosl:
      case Type.Soql: return ' from';
      case Type.Csv:
      case Type.Report: return '';
      case Type.MergeObjects:
      default: return ' in';
    }
  };

  if (Type.Report === type) { return sfObjectName; }

  let name = toDisplayName(type);
  if (sfObjectName) { name += `${getPreposition()} ${sfObjectName}`; }
  if (worksheetName && target) { name += ` at ${worksheetName}!${target}`; }

  return name;
}

export function toStrings(flowstep, worksheetName) {
  const { type: flowstepType, options, name } = flowstep;
  const { pullMode } = flowstep.options;
  const target = flowstep.target ?? flowstep.options.target;
  const er = flowstep.options.errorReaction ?? flowstep.errorReaction;

  function toErrorReaction() {
    switch (er) {
      case ErrorReaction.AskUser: return 'Asks user';
      case ErrorReaction.Continue: return 'Continues'
      case ErrorReaction.Stop: return 'Stops';
    }
  }

  function toPullMode() {
    switch (pullMode) {
      case PullMode.Overwrite: return 'Overwrites existing data';
      case PullMode.Append: return 'Appends to existing data”';
      case PullMode.NewSheet: return 'Creates a new sheet';
      default: return '';
    }
  }

  const isV2 = isNewFlowstepFormat(flowstep);
  const type = isV2 ? null : flowstepType;
  const fullType = isV2
    ? toV2DisplayName(flowstep.originalFlowstep.operation, flowstep.originalFlowstep.provider)
    : toDisplayName(type);

  const fullName = name ?? (Type.Report === type ? options.report_name : '');
  const fullTarget = `${worksheetName}!${target}`; // Sheet!A1

  const flowstepCategory = toCategory({ type, operation: flowstep.originalFlowstep?.operation });
  const description = flowstepCategory === Category.Push ? `${rangeTypeToDisplayName(options.rangeToApply.type)} rows` : '';
  const errorReaction = `${toErrorReaction()} on error`;
  const pullModeText = flowstepCategory === Category.Pull
    ? toPullMode()
    : '';

  const objectText = flowstepCategory === Category.Push
    ? capitalizeString(isV2
      ? flowstep.originalFlowstep.providerOptions.objectType
      : flowstep.options.mapping?.sfObjectName || '')
    : '';

  return { name: fullName, target: fullTarget, type: fullType, description, errorReaction, pullMode: pullModeText, object: objectText };
}

export function isInsertKind(type) {
  return InsertFlowsteps.includes(type);
}

export function isDeleteKind(type) {
  return DeleteFlowsteps.includes(type);
}

export function isPullKind(type) {
  return PullFlowsteps.includes(type);
}

export function isPushKind(type) {
  return PushFlowsteps.includes(type);
}

export function isAdminToolFlowstep(type) {
  return AdminToolFlowsteps.includes(type);
}

export function toCategory({ type, operation } = {}) {
  if (type) {
    return isPullKind(type) ? Category.Pull : Category.Push;
  }
  if (operation) {
    switch (operation) {
      case Operation.PULL: return Category.Pull;
      case Operation.INSERT:
      case Operation.UPDATE:
      case Operation.DELETE:
        return Category.Push;
    }
  }
}

export function generateUuid() {
  return uuid();
}


export class NoDataError extends Error {
  constructor(message) {
    super(message || 'No data is available for this operation');
  }
}

export class TruncateDataError extends Error {
  constructor(message) {
    super(message || 'Some data has been truncated to fit Excel cell size limits');
  }
}

export class ResultDataError extends MultipleErrorsError {
  constructor(errors, range = null) {
    super(errors, 'Results have some errors');
    this.range = range;
  }
};

export class PasswordProtectionError extends Error {
  constructor(scheduler, message = '') {
    super(message ||
      scheduler
      ? 'XL-Connector 365 cannot work with password-protected ranges or worksheets. Please remove the password to be able to run automatic operations in this worksheet.'
      : "Protection password doesn't match with saved in flowstep.");
  }
}

export class NoFlowstepsError extends Error {
  constructor() {
    super('No flowsteps to execute.');
  }
}


// TODO: move to frontend
export function getRoute(
  {
    type = Type.Soql,
    id = '',
    createNew = false,
    range = '',
    flowId = '',
    category = ''
  } = {}
) {
  const qs = toQueryString({ range, createNew, flowId })
  let url = `/home/flowsteps/${type}`;
  // url += category ? `/${category}` : '';
  url += id ? `/${id}` : '';
  url += qs ? `?${qs}` : '';
  return url;
}
